/**
 * MapControlsBar — the map's zoom + view-mode controls as a THIN, persistent
 * bar beneath the map (full-width,
 * paper fill, olive border-y, ~40 px tall) rather than chips floating over the
 * board.
 *
 * Zoom is always available (no on/off mode): a reset + zoom-in + zoom-out
 * cluster acts directly on the current scale while pan/pinch stay live. The
 * view-mode control is a labelled segmented toggle (board vs region bonuses).
 * No group captions — the glyphs + segment labels are self-explanatory.
 *
 * Purely presentational: the parent owns zoom scale / pan + the current mode and
 * passes handlers down. Single thin row in both orientations.
 */
import React from 'react'
import { View, Text, TouchableOpacity } from 'react-native'
import { useMapDimensions } from '../../lib/useMapDimensions'
import { GAME_CONTROL_EDGE_INSET } from '../../lib/gameChrome'
import type { GameState } from '../../lib/gameState'
import { SegmentedToggle, type SegmentOption } from '../ui/SegmentedToggle'

export type MapViewMode = 'game' | 'regions'
const MAP_VIEW_MODE_OPTIONS: readonly SegmentOption<MapViewMode>[] = [
  { value: 'game', label: 'Game' },
  { value: 'regions', label: 'Regions' },
]

/**
 * Wraps the control bar so, in LANDSCAPE, it's exactly as wide as the rendered
 * map (svgWidth) and aligned the same way — instead of stretching across the
 * whole right pane past the letterboxed map's edge. Portrait uses the full
 * width and aligns accessories with the inline chat/log controls. reservedBottom:44 matches the
 * landscape MapView reserves for this bar, so svgWidth matches the map exactly.
 *
 * `fullWidth` opts out of the svgWidth constraint for screens where the map
 * column IS the map area (the /log scrubber + the in-game snapshot view): there
 * the map sits in a header-reduced, measured container that the window-based
 * useMapDimensions can't model, so the bar should simply span the full column
 * beneath the map rather than track a mis-computed svgWidth (which left a dark
 * gutter). The game screen keeps the default alignment (map is letterboxed in a
 * wider pane with a roster gutter, so tracking svgWidth is correct there).
 */
export function MapControlsBarSlot({
  state,
  children,
  fullWidth = false,
}: {
  state: GameState
  children: React.ReactNode
  fullWidth?: boolean
}) {
  const { isLandscape, containerWidth, svgWidth } = useMapDimensions(state, { reservedBottom: 44 })
  const aligned = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<{ portraitActionAlignment?: boolean; compact?: boolean }>, {
      portraitActionAlignment: !isLandscape,
      ...(!isLandscape && containerWidth < 360 ? { compact: true } : {}),
    }) : children
  if (!isLandscape || fullWidth) return <>{aligned}</>
  // The MapView's container is left-aligned in the map pane (its width is
  // containerWidth, which already subtracts the reservedRight gutter), and the
  // SVG is centred inside it with `sidePad` on each side. Match that exactly:
  // left-align the bar and offset it by sidePad + constrain to svgWidth, so it
  // tracks the map's left/right edges instead of the full pane.
  const sidePad = Math.max(0, (containerWidth - svgWidth) / 2)
  // Shrink the controls when the map (and thus the bar) is narrow.
  const childProps = React.isValidElement(children)
    ? children.props as { compact?: boolean; leftAccessory?: unknown; rightAccessory?: unknown }
    : null
  const hasAccessory = !!childProps?.leftAccessory || !!childProps?.rightAccessory
  const compactAt = hasAccessory ? 420 : 320
  const compact = svgWidth < compactAt
  const bar = React.isValidElement(aligned)
    ? React.cloneElement(aligned as React.ReactElement<{ compact?: boolean }>, {
      compact: compact || !!childProps?.compact,
    })
    : aligned
  return (
    <View style={{ alignSelf: 'flex-start', marginLeft: sidePad, width: svgWidth }}>
      {bar}
    </View>
  )
}

export interface MapControlsBarProps {
  /** Reset the zoom + pan back to the default framing. */
  onReset: () => void
  /** Step the zoom in / out by one increment. */
  onZoomIn: () => void
  onZoomOut: () => void
  /** Bound flags — the matching stepper greys out at the min/max scale. */
  canZoomIn: boolean
  canZoomOut: boolean
  /** Current view mode + change handler for the segmented toggle. Omit
   * onModeChange (e.g. the regions-only map viewer) to hide the toggle and
   * show just the centred zoom cluster. */
  mode?: MapViewMode
  onModeChange?: (mode: MapViewMode) => void
  /** Shrink the controls to fit a narrow bar (set by MapControlsBarSlot in
   * landscape when the map's rendered width is small). */
  compact?: boolean
  /** Supplied by the keyboard-stable map slot, never inferred from keyboard height. */
  portraitActionAlignment?: boolean
  /** Independent leading action; mirrors rightAccessory. */
  leftAccessory?: React.ReactNode | ((controlSize: 26 | 30) => React.ReactNode)
  /** Independent trailing action; the main control group stays centred. */
  rightAccessory?: React.ReactNode | ((controlSize: 26 | 30) => React.ReactNode)
  /** Bottom rule colour override — portrait passes a lighter one when the
   *  chat input sits directly beneath, so the two read as one block. */
  bottomRuleColor?: string
}

function PadButton({
  label,
  accessibilityLabel,
  disabled,
  onPress,
  size,
  width,
}: {
  label: string
  accessibilityLabel: string
  disabled?: boolean
  onPress: () => void
  size: number
  width: number
}) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      accessibilityLabel={accessibilityLabel}
      accessibilityRole="button"
      accessibilityState={{ disabled: !!disabled }}
      className="bg-paper border border-olive rounded-md items-center justify-center"
      style={{ width, height: size, opacity: disabled ? 0.4 : 1 }}
    >
      <Text
        className="text-ink"
        // lineHeight == button height + tiny negative nudge centres the glyph
        // vertically (glyph baselines otherwise sit a shade low).
        style={{ fontSize: Math.round(size * 0.56), lineHeight: size, marginTop: -1, textAlign: 'center' }}
      >
        {label}
      </Text>
    </TouchableOpacity>
  )
}

export function MapControlsBar({
  onReset,
  onZoomIn,
  onZoomOut,
  canZoomIn,
  canZoomOut,
  mode,
  onModeChange,
  compact = false,
  portraitActionAlignment = false,
  leftAccessory,
  rightAccessory,
  bottomRuleColor,
}: MapControlsBarProps) {
  // Responsive sizing — `compact` is set by MapControlsBarSlot when the map's
  // rendered width is narrow, so the controls shrink to stay on one row
  // instead of clipping (without flipping to the portrait layout).
  const size = compact ? 26 : 30
  const tight = portraitActionAlignment && !!(leftAccessory || rightAccessory)
  const padW = compact ? (tight ? 27 : 32) : tight ? 32 : 42
  const segMinW = compact ? 38 : tight ? 48 : 58
  const setGap = compact || tight ? 8 : 24
  const renderAccessory = (accessory: MapControlsBarProps['leftAccessory']) =>
    typeof accessory === 'function' ? accessory(size) : accessory

  return (
    <View
      accessibilityLabel="Map controls"
      // Centred group: the zoom cluster, then the view toggle (no divider —
      // saves width). Spacing/sizing shrink in `compact` mode.
      className={`${compact ? 'px-2' : 'px-3'} py-1 bg-paper border-y border-olive flex-row items-center justify-center`}
      style={{
        minHeight: leftAccessory || rightAccessory ? 44 : 40,
        gap: setGap,
        ...(bottomRuleColor ? { borderBottomColor: bottomRuleColor } : null),
      }}
    >
      {/* Independent edge actions stay outside the centered zoom/mode group. */}
      {leftAccessory ? (
        // Match the leading ALL / CHAT control in the revealed chat/log bar.
        <View style={{ position: 'absolute', left: GAME_CONTROL_EDGE_INSET, top: 0, bottom: 0, justifyContent: 'center' }}>
          {renderAccessory(leftAccessory)}
        </View>
      ) : null}
      {/* Zoom cluster — reset (fit whole map) / zoom-in / zoom-out. */}
      <View className="flex-row items-center" style={{ gap: compact ? 4 : 6 }}>
        <PadButton size={size} width={padW} label="⛶" accessibilityLabel="Reset zoom to fit" onPress={onReset} />
        <PadButton
          size={size}
          width={padW}
          label="+"
          accessibilityLabel="Zoom in"
          disabled={!canZoomIn}
          onPress={onZoomIn}
        />
        <PadButton
          size={size}
          width={padW}
          label="−"
          accessibilityLabel="Zoom out"
          disabled={!canZoomOut}
          onPress={onZoomOut}
        />
      </View>

      {/* View-mode toggle — only when a change handler is supplied. The
          regions-only map viewer omits it, leaving the centred zoom cluster. */}
      {onModeChange && (
        <>
          <SegmentedToggle
            size={size}
            minWidth={segMinW}
            compact={compact}
            options={MAP_VIEW_MODE_OPTIONS}
            value={mode}
            onChange={onModeChange}
          />
        </>
      )}
      {rightAccessory ? (
        // Match the trailing grow control in the revealed chat/log bar.
        <View style={{ position: 'absolute', right: GAME_CONTROL_EDGE_INSET, top: 0, bottom: 0, justifyContent: 'center' }}>
          {renderAccessory(rightAccessory)}
        </View>
      ) : null}
    </View>
  )
}
